/**
 * "Últimos focos": os focos mais recentes do conjunto carregado (mesmos
 * filtros do mapa: fontes, Amazônia Legal, satélites). Funções puras.
 *
 * O conjunto já vem ordenado por data, então os mais novos são simplesmente
 * os últimos índices — O(limite), sem varrer os dados.
 */
import { materializeFire, type FireDataset } from './dataset';
import type { VisibleWindow } from './timeFilter';
import type { Fire } from './types';

export const RECENT_LIMIT = 80;

export interface RecentFire {
  /** Índice no conjunto. */
  index: number;
  /** Coordenadas do mapa (projeção do GOES-19). */
  x: number;
  y: number;
  fire: Fire;
}

/** Índices dos `limit` focos mais recentes, do mais novo para o mais antigo. */
export function recentFireIndices(ds: FireDataset, limit = RECENT_LIMIT): number[] {
  const out: number[] = [];
  for (let i = ds.n - 1; i >= 0 && out.length < limit; i--) out.push(i);
  return out;
}

/** Focos mais recentes já prontos para a lista (objetos `Fire`). */
export function recentFires(ds: FireDataset, limit = RECENT_LIMIT): RecentFire[] {
  return recentFiresAt(ds, recentFireIndices(ds, limit));
}

/** Focos dos índices dados, na mesma ordem (ex.: os mais recentes nas áreas do filtro). */
export function recentFiresAt(ds: FireDataset, indices: readonly number[]): RecentFire[] {
  return indices.map((i) => ({ index: i, x: ds.x[i]!, y: ds.y[i]!, fire: materializeFire(ds, i) }));
}

/** Onde o foco está em relação ao que o mapa mostra agora. */
export type FireVisibility = 'visible' | 'later' | 'earlier' | 'unknown';

export function fireVisibility(t: number, w: VisibleWindow | null): FireVisibility {
  if (!w) return 'unknown';
  if (t > w.to) return 'later';
  if (t <= w.from) return 'earlier';
  return 'visible';
}

/**
 * Quadro do loop em que o foco aparece: o primeiro quadro com instante ≥ t
 * (janelas semiabertas (anterior, atual]). −1 quando o foco é posterior ao
 * último quadro ou anterior ao período do loop.
 */
export function frameIndexForFire(frames: readonly number[], t: number, periodFrom: number | null): number {
  if (frames.length === 0) return -1;
  if (periodFrom !== null && t <= periodFrom) return -1;
  let lo = 0;
  let hi = frames.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (frames[mid]! < t) lo = mid + 1;
    else hi = mid;
  }
  return lo < frames.length ? lo : -1;
}
